import React from "react";
import { defineMessage, useIntl } from "react-intl";
import ColorLegend from "../charts/lib/ColorLegend";
import { Line as FixedWidthLineChart } from "../charts/lib/Lines";
import { ChartGrid } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_quality_fig_PTR";
import { datasets } from "../data/data_quality_fig_PTR_datasets";
import { countryIdIso, foldGeoEntity, getCountryName, getLevelName, mkGemEntityDecoder, ordLevel, useButtonGroupState, useCountryEntities, useFigureControlItems, useFigureState, useMultiSelectState, useNamedEntities } from "../domain";
import { useMedia, useOrderedColorScale } from "../hooks";
import { unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { O, pipe } from "../prelude";
export * from "../data/data_quality_fig_PTR";
const DEFAULT_COUNTRIES = [
    "BDI",
    "MLI",
    "UGA"
];
const DEFAULT_LEVEL = "primary";
export const Data = mkGemEntityDecoder([
    "ind_id",
    "level",
    "year",
    "value"
], {});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedLevel] = useButtonGroupState("a:level");
    const [selectedCountries] = useMultiSelectState("b:countries");
    const entityData = useNamedEntities(data);
    const countryData = useCountryEntities(entityData);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    const levelControlItems = useFigureControlItems(entityData, (x)=>x.level, (x)=>getLevelName(intl, x.level), {
        ord: ordLevel
    });
    React.useEffect(()=>{
        actions.setSelectionControl("a:level", {
            type: "ButtonGroup",
            selected: DEFAULT_LEVEL
        }, levelControlItems);
        actions.setSelectionControl("b:countries", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Countries",
                defaultMessage: "Countries"
            })),
            selected: O.some(DEFAULT_COUNTRIES)
        }, countryControlItems);
    }, [
        intl,
        actions,
        countryControlItems,
        levelControlItems
    ]);
    const chartData = React.useMemo(()=>{
        const hasCountry = (country)=>pipe(selectedCountries, O.fold(()=>false, (xs)=>xs.includes(countryIdIso.unwrap(country))));
        return unsafeFromArray(entityData.filter(foldGeoEntity({
            country: ({ id, level })=>hasCountry(id) && level === selectedLevel,
            region: ({ entity_type, level })=>entity_type === "geo" && level === selectedLevel
        })));
    }, [
        entityData,
        selectedCountries,
        selectedLevel
    ]);
    const selectedCountryLabels = React.useMemo(()=>{
        return pipe(selectedCountries, O.map((xs)=>xs.map((x)=>getCountryName(env.countries, countryIdIso.wrap(x)))));
    }, [
        env.countries,
        selectedCountries
    ]);
    const { colorScale, colorLegendValues } = useOrderedColorScale(selectedCountryLabels);
    return <ChartGrid data={chartData} getCell={React.useCallback((x)=>x.entity_type_name, [])} columnCount={2} minWidth={100}>
      {({ data, width, first })=>{
        return <>
            <FixedWidthLineChart tLabel={(s)=>s} width={width} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} endLabel={!screenMDown} x="year" timeParse="%Y" timeFormat="%Y" numberFormat={".0f"} yTicks={[
            0,
            20,
            40,
            60,
            80,
            100
        ]} category="datum.entity_name" colorLegend={!first} colorScale={first ? colorScale : undefined} values={data} connectLines showTooltip/>
            {first && <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>}
          </>;
    }}
    </ChartGrid>;
};
export default withFigureIO({
    url: new URL("../data/data_quality_fig_PTR.json", import.meta.url).href,
    csv: new URL("../data/data_quality_fig_PTR.zip", import.meta.url).href,
    xlsx: new URL("../data/data_quality_fig_PTR.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
